import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useChatroom } from '../../contexts/ChatroomContext';
import { Message } from './Message';
import { BidResults } from './BidResults';
import { BidCollecting } from './BidCollecting';
import './MessagePane.css';

export function MessagePane() {
  const { activeChannelId, messages, typingUsers, channels, bidCycleState, bidResults, bidResultsByCycle, participants } = useChatroom();
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Track if user has scrolled away from bottom
  const [isNearBottom, setIsNearBottom] = useState(true);
  const isNearBottomRef = useRef(true); // Also keep a ref for use in effects
  const scrollTimeoutRef = useRef(null); // Debounce auto-scroll

  const channelMessages = activeChannelId ? (messages[activeChannelId] || []) : [];
  const typingSet = activeChannelId ? (typingUsers[activeChannelId] || new Set()) : new Set();
  const activeChannel = channels.find(ch => ch.id === activeChannelId);
  const currentBidState = activeChannelId ? (bidCycleState[activeChannelId] || { status: 'idle' }) : { status: 'idle' };
  const currentBidResults = activeChannelId ? bidResults[activeChannelId] : null;
  const channelParticipants = activeChannelId ? (participants[activeChannelId] || []) : [];
  const llmParticipants = channelParticipants.filter(p => p.participant_type === 'llm' && p.is_active);

  // Check if scroll position is near bottom (within threshold)
  const checkIfNearBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return true;

    const threshold = 150; // pixels from bottom
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    return distanceFromBottom <= threshold;
  }, []);

  // Handle scroll events to track user's scroll position
  const handleScroll = useCallback(() => {
    const nearBottom = checkIfNearBottom();
    setIsNearBottom(nearBottom);
    isNearBottomRef.current = nearBottom;
  }, [checkIfNearBottom]);

  // Auto-scroll to bottom only if user was already near bottom
  useEffect(() => {
    // Clear any pending scroll
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Debounce rapid updates during streaming (wait 50ms for updates to settle)
    scrollTimeoutRef.current = setTimeout(() => {
      if (isNearBottomRef.current) {
        // Use 'auto' instead of 'smooth' to avoid animation conflicts during streaming
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
      }
    }, 50);

    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [channelMessages, currentBidState.status, currentBidResults]);

  // Reset to bottom when changing channels
  useEffect(() => {
    setIsNearBottom(true);
    isNearBottomRef.current = true;
    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
  }, [activeChannelId]);

  // Scroll to bottom handler for the "new messages" button
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    setIsNearBottom(true);
    isNearBottomRef.current = true;
  }, []);

  if (!activeChannelId) {
    return (
      <div className="message-pane empty">
        <div className="empty-state">
          <h2>Welcome to Chatrooms</h2>
          <p>Select a channel from the sidebar to start chatting</p>
        </div>
      </div>
    );
  }

  return (
    <div className="message-pane">
      <div className="messages" ref={messagesContainerRef} onScroll={handleScroll}>
        {channelMessages.length === 0 ? (
          <div className="no-messages">
            <p>No messages yet. Be the first to say something!</p>
          </div>
        ) : (
          channelMessages.map(message => {
            // For LLM messages, get bid results from:
            // 1. message.bid_results (from backend, persisted)
            // 2. bidResultsByCycle (from WebSocket during this session)
            const cycleId = message.metadata?.cycle_id;
            const messageBidResults = message.bid_results
              || (cycleId ? bidResultsByCycle[cycleId] : null);
            return (
              <Message
                key={message.id}
                message={message}
                bidResults={messageBidResults}
              />
            );
          })
        )}
        {/* Show bid collection status or results during active bid cycle */}
        {/* Hide once a message with this cycle_id exists (streaming has started) */}
        {currentBidState.status === 'collecting' && !currentBidResults && (
          <BidCollecting participants={llmParticipants} />
        )}
        {currentBidResults && currentBidState.status !== 'idle' && (
          // Check if any message already has this cycle_id - if so, don't show standalone
          !channelMessages.some(m => m.metadata?.cycle_id === currentBidResults.cycleId) && (
            <BidResults bidResults={currentBidResults} />
          )
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Scroll to bottom button - appears when user has scrolled up */}
      {!isNearBottom && (
        <button className="scroll-to-bottom" onClick={scrollToBottom} title="Scroll to bottom">
          ↓ New messages
        </button>
      )}

      {/* Bid cycle status indicator */}
      {currentBidState.status !== 'idle' && (
        <div className={`bid-cycle-status status-${currentBidState.status}`}>
          {currentBidState.status === 'collecting' && (
            <span>Collecting bids from {currentBidState.participantCount} participants...</span>
          )}
          {currentBidState.status === 'responding' && (
            <span>{currentBidState.respondingParticipant} is responding...</span>
          )}
          {currentBidState.status === 'dormant' && (
            <span>Conversation dormant (max bid: {Math.round(currentBidState.maxBid)} &lt; threshold: {currentBidState.threshold})</span>
          )}
        </div>
      )}

      {typingSet.size > 0 && (
        <div className="typing-indicator">
          <span>Someone is typing...</span>
        </div>
      )}
    </div>
  );
}
